import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  useOrgUsage,
  useOrgUsageReport,
  useOrgUsageServiceReport,
  useOrgUsageServiceReportByUsers,
  useSetMemberBudget,
} from '@/hooks/org-admin';
import { TimeRangeToggle } from '@/components/molecules/org-admin/TimeRangeToggle';
import {
  MemberBudgetEditModal,
  type MemberBudgetEditModalTarget,
} from '@/components/molecules/org-admin/MemberBudgetEditModal';
import type { OrgAdminSelfContext, UsageWindowDays } from '@/components/molecules/org-admin/types';
import type { OrgUsageReportMember, SetMemberBudgetInput } from '@/services/org-admin';
import { OrgUsageSummaryPanel } from './OrgUsageSummaryPanel';
import { MemberUsageChart } from './MemberUsageChart';
import { UsageByServiceChart } from './UsageByServiceChart';

export interface TokenUsagePanelProps {
  self: OrgAdminSelfContext;
}

// Matches the backend's own default (`UsageController.@default_report_window`).
const DEFAULT_WINDOW: UsageWindowDays = 30;

export function TokenUsagePanel({ self }: TokenUsagePanelProps) {
  const isAdmin = self.currentUserRole === 'owner' || self.currentUserRole === 'admin';
  const [days, setDays] = useState<UsageWindowDays>(DEFAULT_WINDOW);
  const [budgetTarget, setBudgetTarget] = useState<MemberBudgetEditModalTarget | null>(null);

  const { data: rollup, isLoading: isRollupLoading, isError: isRollupError, error: rollupError } = useOrgUsage();
  const {
    data: report,
    isLoading: isReportLoading,
    isError: isReportError,
    error: reportError,
  } = useOrgUsageReport(days, { enabled: isAdmin });
  const {
    data: serviceReport,
    isLoading: isServiceReportLoading,
    isError: isServiceReportError,
    error: serviceReportError,
  } = useOrgUsageServiceReport(days, { enabled: isAdmin });
  const {
    data: serviceReportByUsers,
    isLoading: isServiceReportByUsersLoading,
    isError: isServiceReportByUsersError,
    error: serviceReportByUsersError,
  } = useOrgUsageServiceReportByUsers(days, { enabled: isAdmin });
  const setBudget = useSetMemberBudget();

  const periodLabel = `${days}-day window`;
  const isLoading = isAdmin ? isRollupLoading || isReportLoading : isRollupLoading;

  const handleEditBudget = (member: OrgUsageReportMember) => {
    setBudgetTarget({ user_id: member.user_id, email: member.email, budget_usd: member.budget_usd });
  };

  const handleBudgetSubmit = async (data: SetMemberBudgetInput) => {
    if (!budgetTarget) return;
    try {
      await setBudget.mutateAsync({ userId: budgetTarget.user_id, input: data });
      toast.success('Budget updated');
      setBudgetTarget(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update budget');
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4">
      <div className="flex shrink-0 items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {isAdmin ? 'Spend, tokens and budgets across the org.' : "Your organization's spend rollup."}
        </p>
        {isAdmin && <TimeRangeToggle value={days} onChange={setDays} />}
      </div>

      {isRollupError && (
        <p className="mt-4 text-sm text-destructive">
          {(rollupError as Error)?.message ?? 'Failed to load usage data.'}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-4">
        <OrgUsageSummaryPanel
          isAdmin={isAdmin}
          periodLabel={periodLabel}
          rollup={rollup}
          report={isAdmin ? report : undefined}
          isLoading={isLoading}
        />

        {isAdmin && isReportError && (
          <p className="text-sm text-destructive">
            {(reportError as Error)?.message ?? 'Failed to load the token usage report.'}
          </p>
        )}

        {isAdmin && isServiceReportError && (
          <p className="text-sm text-destructive">
            {(serviceReportError as Error)?.message ?? 'Failed to load the service usage report.'}
          </p>
        )}

        {isAdmin && isServiceReportByUsersError && (
          <p className="text-sm text-destructive">
            {(serviceReportByUsersError as Error)?.message ?? 'Failed to load the per-member service usage report.'}
          </p>
        )}

        {isAdmin && (
          <div className="flex flex-col gap-4">
            {!isReportError && (
              <MemberUsageChart
                data={report?.members ?? []}
                periodLabel={periodLabel}
                isLoading={isReportLoading}
                onEditBudget={handleEditBudget}
                serviceByUsers={serviceReportByUsers?.users ?? []}
                isServiceByUsersLoading={isServiceReportByUsersLoading}
              />
            )}
            {!isServiceReportError && (
              <UsageByServiceChart
                data={serviceReport?.by_service ?? []}
                periodLabel={periodLabel}
                isLoading={isServiceReportLoading}
              />
            )}
          </div>
        )}
      </div>

      <MemberBudgetEditModal
        open={!!budgetTarget}
        member={budgetTarget}
        isSubmitting={setBudget.isPending}
        onSubmit={handleBudgetSubmit}
        onClose={() => setBudgetTarget(null)}
      />
    </div>
  );
}
